// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Checkbox, message, Tabs } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  formateDateTime,
  handleDelete,
  truncateWords,
} from "../../utils/Constants";
import {
  deleteNotificationThunk,
  getNotificationListThunk,
  readAllNotificationsThunk,
  readSingleNotificationThunk,
  updateNotificationThunk,
} from "../../store/thunks/notificationsThunk";
import media from "../../media";
import dayjs from "dayjs";
import CustomLoader from "../../utils/CustomLoader";
const { TabPane } = Tabs;

// --------------------------
// MAIN COMPONENT
// --------------------------
const Notifications = () => {
  //Dispatch + Custom Hooks
  //const { id: notificationId } = useParams();
  const location = useLocation();
  //const params = new URLSearchParams(location.search);
  //const notificationId = params.get("notificationId");
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const notifications = useSelector((state) => state.notifications.list) || [];

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [notificationId, setNotificationId] = useState(null);
  const [storeTotalCount, setStoreTotalCount] = useState(0);  
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [tabCounts, setTabCounts] = useState({ tab1: 0, tab2: 0, tab3: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMorePendingVisibleItemCount, setLoadMorePendingVisibleItemCount] =
    useState(0);
  const [tabPage, setTabPage] = useState({ tab1: 1, tab2: 1, tab3: 1 });
  const [tabHasMore, setTabHasMore] = useState({
    tab1: true,
    tab2: true,
    tab3: true,
  });
  const [pageSize, setPageSize] = useState(100);

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------

  const tabPaneList = [
    { key: "1", tabName: "All", tabImage: media.all.svg },
    { key: "2", tabName: "New", tabImage: media.new.svg },
    { key: "3", tabName: "Unread", tabImage: media.unread.svg },
  ];

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch notifications
  const fetchNotifications = async () => {    
    try {
      const res = await dispatch(
        getNotificationListThunk({
          route: currentPath,
          role: storeRole,
          // pagination: {
          //   current: 1,
          //   pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
          // },
        })
      ).unwrap();

      setStoreTotalCount(res.totalCount || 0);      
      if (res?.data?.length > 0) {
        setFilteredNotifications(res.data);
        if (notificationId) {
          const found = res.data.find((n) => n.key === notificationId);

          if (found) {
            setActiveMenu({ 1: notificationId });

            // mark as read (only if needed)
            handleNotificationClick(found, "1");

            // scroll into view after list renders
            setTimeout(() => {
              const ele = document.getElementById(`noti-${notificationId}`);
              if (ele)
                ele.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 300);
          }
          if (!found && filteredNotifications.length > 0) {
            console.warn("Notification not in list — fetching it manually");

            try {
              const single = await dispatch(
                readSingleNotificationThunk({
                  route: currentPath,
                  method: "GET",
                  id: notificationId,
                })
              ).unwrap();

              if (single) {
                setActiveTabKey("1");
                // Put it at the top
                setFilteredNotifications((prev) => [single, ...prev]);

                // Activate it
                setActiveMenu({ 1: notificationId });
                handleNotificationClick(notificationId, "1");

                // Scroll after DOM update
                setTimeout(() => {
                  const el = document.getElementById(`noti-${notificationId}`);
                  if (el)
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 300);
              }
            } catch (e) {
              console.error("Notification does not exist anymore");
            }
          }
        } else {
          // Default case when no link ID
          setActiveMenu({ tab1: res.data[0].key });
          handleNotificationClick(res.data[0], "1");
        }
        setLoadMorePendingVisibleItemCount(
          res.totalCount &&
            res.data.length &&
            Number(res.totalCount) - Number(res.data.length)
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setShowLoader(false); // hide loader
    }
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  useEffect(() => {
    setNotificationId(location.state?.notificationId || null);
  }, [location]);

  useEffect(() => {
    fetchNotifications();
  }, [currentPath, notificationId]);

  useEffect(() => {
    if (!notifications || notifications.length === 0) return;

    // Sync filteredNotifications
    setFilteredNotifications(notifications);

    // Initialize tab active keys & pages
    setActiveMenu({
      tab1: notifications[0]?.key || null,
      tab2: notifications.find((n) => n.isRead === "Read")?.key || null,
      tab3: notifications.find((n) => n.isRead === "Un-read")?.key || null,
    });
    setTabPage({ tab1: 1, tab2: 1, tab3: 1 });

    // Automatically open first notification in "All" tab
    if (notificationId) {
      const found = notifications.find(
        (n) => n.key === notificationId || n.id === notificationId
      );
      if (found) {
        setActiveMenu({ tab1: found.key });
        handleNotificationClick(found, "1");
      }
    } else if (activeTabKey === "1") {
      const firstItem = notifications[0];
      if (firstItem) handleNotificationClick(firstItem, "1");
    }
  }, [notifications, activeTabKey]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const loadMoreNotifications = async () => {
    const tabKey = activeTabKey;
    const tabStateKey = `tab${tabKey}`;

    if (loadingMore || !tabHasMore[tabStateKey]) return;

    setLoadingMore(true);
    const nextPage = tabPage[tabStateKey] + 1;

    let filterValues = "";
    if (tabKey === "2") filterValues = "new";
    if (tabKey === "3") filterValues = "unread";

    try {
      const res = await dispatch(
        getNotificationListThunk({
          route: currentPath,
          role: storeRole,
          filterValues,
          pagination: {
            current: nextPage,
            pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
          },
        })
      ).unwrap();

      const newData = res?.data || [];

      if (newData.length > 0) {
        // Append new notifications and update tabPage at the same time
        setFilteredNotifications((prev) => {
          const updatedList = [...prev, ...newData];

          // update remaining count using updatedList length (no stale reads)
          setLoadMorePendingVisibleItemCount(
            Math.max(0, Number(storeTotalCount || 0) - updatedList.length)
          );

          return updatedList;
        });
        setTabPage((prev) => ({ ...prev, [tabStateKey]: nextPage }));

        // // Update remaining count
        // setLoadMorePendingVisibleItemCount(
        //   Number(storeTotalCount) - (filteredNotifications.length + newData.length)
        // );

        // Update hasMore for Load More button
        setTabHasMore((prev) => ({
          ...prev,
          [tabStateKey]:
            newData.length ===
            Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE),
        }));
      } else {
        setTabHasMore((prev) => ({ ...prev, [tabStateKey]: false }));
        setLoadMorePendingVisibleItemCount(0);
      }
    } catch (error) {
      console.error("Error loading more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleReadAll = () => {
    setShowLoader(true);
    let filterValues = "";
    if (activeTabKey === "2") filterValues = "new";
    if (activeTabKey === "3") filterValues = "unread";

    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
      action: "readAll",
      route: currentPath,
      filterValues,
    };

    dispatch(readAllNotificationsThunk(payload))
      .unwrap()
      .then(() => {
        message.success("All notifications are marked as read.");
      })
      .catch((error) => {
        message.error(error?.data?.message || "Failed to mark as read.");
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  let totalCount =
    notifications && notifications.filter((list) => list.isRead === "Un-read");
  totalCount = totalCount && totalCount.length;
  const [activeMenu, setActiveMenu] = useState({});

  const handleNotificationClick = async (item, tabKey) => {
    //Highlight clicked item
    setActiveMenu((prev) => ({ ...prev, [tabKey]: item.key }));

    if (item.isRead === "Read") return;

    // Hit API to mark as read
    try {
      await dispatch(
        updateNotificationThunk({
          route: currentPath,
          id: item.key ? item.key : item,
          isRead: "Read", // send as "Read" or 1 depending on your API
        })
      ).unwrap();

      // // Update local state to reflect read status
      // setFilteredNotifications((prev) =>
      //   prev.map((n) =>
      //     n.key === item.key
      //       ? { ...n, isRead: "Read" } // mark locally
      //       : n
      //   )
      // );

      // Optionally update tabCounts for "Unread" tab
      // setTabCounts((prev) => ({
      //   ...prev,
      //   tab3: prev.tab3 - 1 >= 0 ? prev.tab3 - 1 : 0,
      // }));
    } catch (error) {
      message.error("Failed to mark notification as read.");
    }
  };

  const handleTabChange = async (key) => {
    setActiveTabKey(key);
    setSelectedItems([]); // reset selection

    // Determine filter based on tab key
    let filterType = "";
    if (key === "1") filterType = "";
    if (key === "2") filterType = "new";
    if (key === "3") filterType = "unread";

    // Status mapping for API
    const statusMap = {
      2: "new",
      3: "unread",
    };

    let res;

    if (key === "1") {
      // All tab
      res = await dispatch(
        getNotificationListThunk({
          route: currentPath,
          pagination: {
            current: 1,
            pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
          },
        })
      ).unwrap();
      setStoreTotalCount(res.totalCount || 0);
    } else {
      // New / Unread tab
      res = await dispatch(
        getNotificationListThunk({
          route: currentPath,
          filterValues: statusMap[key],
          pagination: {
            current: 1,
            pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
          },
        })
      ).unwrap();
    }

    // Update state with fetched data
    setFilteredNotifications(res.data || []);

    setLoadMorePendingVisibleItemCount(
      res.totalCount &&
        res.data.length &&
        Number(res.totalCount) - Number(res.data.length)
    );
    setActiveMenu({ [key]: res.data?.[0]?.key || null });
    setStoreTotalCount(res.totalCount || 0);

    setTabCounts((prev) => ({
      ...prev,
      [`tab${key}`]: res.totalCount || 0,
    }));
  };

  const getFilteredNotifications = (tabKey) => {
    const today = dayjs().format("YYYY-MM-DD");
    const source = filteredNotifications;
    if (tabKey === "1") return source; // All
    if (tabKey === "2") {
      // New: unread and created today
      return source.filter(
        (n) => n.isRead === "Un-read" && n.createdAt
        //&&
        // dayjs(n.createdAt).format("YYYY-MM-DD") === today
      );
    }
    if (tabKey === "3") {
      // Un-read: all unread
      return source.filter((n) => n.isRead === "Un-read");
    }
    return [];
  };

  const getPaginatedNotifications = (tabKey) => {
    const filtered = getFilteredNotifications(tabKey);
    const page = tabPage[`tab${tabKey}`] || 1;
    const start = (page - 1) * pageSize;
    //return filtered.slice(start, start + pageSize);

    const end = page * pageSize;
    return filtered.slice(0, end);
  };

  const getCurrentTabKeys = () => {
    const items = getPaginatedNotifications(activeTabKey);
    return items.map((item) => item.key);
  };
  // Compute Select All checkbox state
  const currentKeys = getCurrentTabKeys();
  const isAllSelected =
    selectedItems.length === currentKeys.length && currentKeys.length > 0;
  const isIndeterminate =
    selectedItems.length > 0 && selectedItems.length < currentKeys.length;

  const renderVerticalMenu = (tabKey) => {
    const filtered = getFilteredNotifications(tabKey);
    const menuItems = getPaginatedNotifications(tabKey).map((item) => {
      return {
        key: item.key,
        title: item.title,
        subtext: (
          <div
            dangerouslySetInnerHTML={{
              __html: truncateWords(item.message, 10),
            }}
          />
        ),
        type: item.natureOfExpense,
        createdAt: item.createdAt,
        details: <div dangerouslySetInnerHTML={{ __html: item.message }} />,
        assignedBy: item.assignedBy,
        assignedByProfilePicUrl: item.assignedByProfilePicUrl,
        assignedTo: item.assignedTo,
        assignedToProfilePicUrl: item.assignedToProfilePicUrl,
        isRead: item.isRead,
      };
    });

    return (
      <>        
        {showLoader ? (
          <CustomLoader showLoader={true} />
        ) : (
          <div className="customTabsWrap">
            {/* LEFT: Vertical Menu */}
            <div
              className="customTabsWrapLeft notification-list"
              style={{
                overflowY: "auto",
                borderRight: "1px solid #f0f0f0",
              }}
            >
              {menuItems.map((item) => (
                <div
                  key={item.key}
                  id={`noti-${item.key}`}
                  className={
                    activeMenu[tabKey] === item.key
                      ? "activeCustomTabHead customTabHead"
                      : "customTabHead"
                  }
                  style={{ cursor: "pointer" }}
                  onClick={() => handleNotificationClick(item, tabKey)}
                >
                  <div className="customTabHeadContent">
                    <Checkbox
                      checked={selectedItems.includes(item.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems((prev) => [...prev, item.key]);
                        } else {
                          setSelectedItems((prev) =>
                            prev.filter((k) => k !== item.key)
                          );
                        }
                      }}
                      onClick={(e) => e.stopPropagation()} // prevent parent click
                    />
                    <div className="customTabText">
                      {/* {item.isRead === "Un-read" ? "redDot" : "greenDot"}{" "}
                  {item.isRead} */}
                      <span
                        className={`notiStatus ${
                          item.isRead === "Un-read" ? "redDot" : "greenDot"
                        }`}
                        style={{
                          background: `${
                            item.isRead === "Un-read" ? "#A80000" : "#00a822ff"
                          }`,
                        }}
                      ></span>
                      <h4>{item.title}</h4>
                      <span>{item.subtext}</span>{" "}
                      {/*change from p tag to span tag as div cann't be descendant of p tag */}
                      <div className="customStatus mt-2">
                        <span>{item.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="customTabTime">
                    <span>
                      {item.createdAt && formateDateTime(item.createdAt)}
                    </span>
                  </div>
                </div>
              ))}

              <div className="loadBtn">
                <button
                  className="btn mx-auto px-5 py-2 rounded-lg defalutWhiteBtn"
                  onClick={(e) => {
                    e.preventDefault();
                    loadMoreNotifications();
                  }}
                  disabled={loadingMore || !tabHasMore[`tab${activeTabKey}`]}
                >
                  {loadingMore && loadMoreVisible ? (
                    <>
                      <img
                        src={media.loading.svg}
                        alt="Loading"
                        className="mr-2"
                      />
                      Loading...
                    </>
                  ) : loadMoreVisible ? (
                    <>
                      <img
                        src={media.loading.svg}
                        alt="Load More"
                        className="mr-2"
                      />
                      Load More
                    </>
                  ) : (
                    "No More Notifications"
                  )}
                </button>
              </div>
            </div>
            {/* Pagination */}
            {/* {filtered.length > pageSize && renderPagination(tabKey, filtered.length)} */}

            {/* RIGHT: Content Area */}
            <div className="customTabsWrapRight">
              {(() => {
                const activeItem = menuItems.find(
                  (item) => item.key === activeMenu[tabKey]
                );
                if (!activeItem) return <p className="noSelection"></p>;

                const initialAssignedTo = activeItem?.assignedTo
                  ? activeItem.assignedTo.charAt(0).toUpperCase()
                  : "?";
                const imageUrlAssignedTo = activeItem?.assignedToProfilePicUrl
                  ? process.env.REACT_APP_BACKEND +
                    "/images/" +
                    activeItem.assignedToProfilePicUrl
                  : media.user.png;
                const initialAssignedBy = activeItem?.assignedBy
                  ? activeItem.assignedBy.charAt(0).toUpperCase()
                  : "?";
                const imageUrlAssignedBy = activeItem?.assignedByProfilePicUrl
                  ? process.env.REACT_APP_BACKEND +
                    "/images/" +
                    activeItem.assignedByProfilePicUrl
                  : media.user.png;

                return (
                  <div className="headerBodyWrap" key={activeItem.key}>
                    <div className="headerBodyContent">
                      <div className="notiHeader">
                        <h3>{activeItem.title}</h3>
                        <ul className="notimeta">
                          <li>
                            {activeItem.createdAt &&
                              formateDateTime(activeItem.createdAt)}
                          </li>
                          <li>
                            <div className="customStatus">
                              <span>{activeItem.type}</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div className="notiBody">{activeItem.details}</div>
                    </div>

                    <div className="tabsFooter">
                      <div className="approverList">
                        <h4>Assigned By :</h4>
                        <ul>
                          <li>Name:</li>
                          <li>
                            <span className="approvers-status-tooltip">
                              {imageUrlAssignedBy &&
                              activeItem?.assignedBy !== "System Generated" ? (
                                <img
                                  src={imageUrlAssignedBy}
                                  alt={activeItem?.assignedBy || "Employee"}
                                />
                              ) : (
                                <span className="initialLetter">
                                  {initialAssignedBy}
                                </span>
                              )}
                              <span></span>
                            </span>
                            <h5>{activeItem.assignedBy}</h5>
                          </li>
                        </ul>
                      </div>

                      <div className="approverList">
                        <h4>Assigned To :</h4>
                        <ul>
                          <li>Name:</li>
                          <li>
                            <span className="approvers-status-tooltip">
                              {imageUrlAssignedTo &&
                              activeItem?.assignedTo !== "System Generated" ? (
                                <img
                                  src={imageUrlAssignedTo}
                                  alt={activeItem?.assignedTo || "Employee"}
                                />
                              ) : (
                                <span className="initialLetter">
                                  {initialAssignedTo}
                                </span>
                              )}
                              <span></span>
                            </span>
                            <h5>{activeItem.assignedTo}</h5>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </>
    );
  };

  const loadMoreVisible = loadMorePendingVisibleItemCount != 0 ? true : false;

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      {/* <div>
        <h4>Selected Items:</h4>
        <pre>{JSON.stringify(selectedItems, null, 2)}</pre>
      </div> */}
      <div className="CusnotiHead">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Notifications"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={false}
        />
      </div>

      <div className="customTabs">
        <div className="checkDelete">
          <Checkbox
            indeterminate={isIndeterminate}
            aria-label="Select all"
            checked={isAllSelected}
            onChange={(e) => {
              setSelectedItems(e.target.checked ? currentKeys : []);
            }}
          >
            All
          </Checkbox>
          {totalCount > 0 && (
            <Link className="seeAll" onClick={handleReadAll}>
              Read All
            </Link>
          )}
          {storeRole === process.env.REACT_APP_SROLE &&
            selectedItems.length > 0 && (
              <span
                className="notiDelete"
                onClick={() => {
                  handleDelete({
                    selectedRowKeys: selectedItems,
                    dispatch,
                    deleteThunk: deleteNotificationThunk,
                    route: currentPath,
                    actionName: "notification",
                    onSuccess: () => {
                      setFilteredNotifications((prev) =>
                        prev.filter((item) => !selectedItems.includes(item.key))
                      );
                      setSelectedItems([]);
                      setStoreTotalCount((prev) => prev - selectedItems.length);
                      setTabCounts((prev) => ({
                        ...prev,
                        [`tab${activeTabKey}`]:
                          (prev[`tab${activeTabKey}`] || 0) -
                          selectedItems.length,
                      }));

                      // Reset activeMenu if needed
                      if (selectedItems.includes(activeMenu[activeTabKey])) {
                        setActiveMenu({
                          [activeTabKey]: filteredNotifications[0]?.key || null,
                        });
                      }
                    },
                  });
                }}
              >
                <img src={media.delete.svg} alt="All" /> &nbsp;Delete (
                {selectedItems.length})
              </span>
            )}
        </div>

        <Tabs
          activeKey={activeTabKey}
          type="line"
          onChange={handleTabChange}
          items={tabPaneList.map((tab) => ({
            key: tab.key,
            label: (
              <span>
                <img
                  src={tab.tabImage}
                  alt={tab.tabName}
                  style={tab.tabStyle}
                />
                {tab.tabName}
                {/* {tab.tabName} ({tabCounts[`tab${tab.key}`] || 0}) */}
              </span>
            ),
            // children: Array.isArray(tab.menus)
            //   ? renderVerticalMenu(tab.key)
            //   : null,
            children: renderVerticalMenu(tab.key),
          }))}
        />
      </div>
    </div>
  );
};

export default Notifications;
